import React, { useState } from "react";

const GRANT_API = process.env.REACT_APP_CLAIM_API || "http://localhost:3032";

const GRANT_TYPES = ["contributor", "advisor", "influencer", "marketing", "investor", "team"];

const BECH32_CHARSET = "qpzry9x8gf2tvdw0s3jn54khce6mua7l";

function bech32Polymod(values) {
  const G = [0x3b6a57b2, 0x26508e6d, 0x1ea119fa, 0x3d4233dd, 0x2a1462b3];
  let chk = 1;
  for (const v of values) {
    const b = chk >>> 25;
    chk = ((chk & 0x1ffffff) << 5) ^ v;
    for (let i = 0; i < 5; i++) if ((b >>> i) & 1) chk ^= G[i];
  }
  return chk >>> 0;
}

function isMainnetAddress(address) {
  if (!/^ego1[qpzry9x8gf2tvdw0s3jn54khce6mua7l]{40}$/.test(address)) return false;
  const hrp = "ego";
  const data = [...address.slice(4)].map((c) => BECH32_CHARSET.indexOf(c));
  const expanded = [...hrp].map((c) => c.charCodeAt(0) >> 5)
    .concat([0], [...hrp].map((c) => c.charCodeAt(0) & 31));
  return bech32Polymod(expanded.concat(data)) === 0x2bc830a3;
}

const s = {
  page: {
    background: "#0a0a0f",
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "24px",
    paddingTop: "100px",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    color: "#e2e8f0",
  },
  card: {
    background: "#111118",
    border: "1px solid #1e1e2e",
    borderRadius: "20px",
    padding: "40px",
    width: "100%",
    maxWidth: "520px",
  },
  header: { marginBottom: "28px" },
  badge: {
    display: "inline-flex", alignItems: "center", gap: "6px",
    background: "#1a1040", border: "1px solid #6366f133",
    borderRadius: "20px", padding: "4px 12px",
    fontSize: "11px", fontWeight: 600, color: "#a78bfa",
    marginBottom: "12px",
  },
  h1: { fontSize: "22px", fontWeight: 800, marginBottom: "6px" },
  subtitle: { fontSize: "13px", color: "#64748b", lineHeight: 1.5 },
  field: { marginBottom: "16px" },
  label: { display: "block", fontSize: "12px", fontWeight: 600, color: "#94a3b8", marginBottom: "6px" },
  input: {
    width: "100%", background: "#1a1a2e", border: "1px solid #2d2d4e",
    borderRadius: "10px", padding: "12px 14px", fontSize: "14px",
    color: "#e2e8f0", outline: "none", boxSizing: "border-box",
  },
  select: {
    width: "100%", background: "#1a1a2e", border: "1px solid #2d2d4e",
    borderRadius: "10px", padding: "12px 14px", fontSize: "14px",
    color: "#e2e8f0", outline: "none", boxSizing: "border-box",
    appearance: "none", cursor: "pointer",
  },
  row: { display: "flex", gap: "12px" },
  divider: { borderColor: "#1e1e2e", margin: "20px 0" },
  secretNote: { fontSize: "11px", color: "#475569", marginTop: "4px" },
  btn: (disabled) => ({
    width: "100%", padding: "14px", borderRadius: "12px", border: "none",
    fontSize: "15px", fontWeight: 700,
    cursor: disabled ? "not-allowed" : "pointer",
    background: "linear-gradient(135deg, #6366f1, #a855f7)",
    color: "#fff", marginTop: "8px",
    opacity: disabled ? 0.4 : 1,
    transition: "opacity 0.2s",
  }),
  errorBox: {
    background: "#2d1515", border: "1px solid #7f1d1d",
    borderRadius: "10px", padding: "12px 14px",
    fontSize: "13px", color: "#fca5a5", marginTop: "16px",
  },
  result: {
    background: "#0d1f0d", border: "1px solid #166534",
    borderRadius: "14px", padding: "20px", marginTop: "20px",
  },
  resultTitle: { fontSize: "14px", fontWeight: 700, color: "#4ade80", marginBottom: "14px" },
  resultRow: { display: "flex", justifyContent: "space-between", marginBottom: "8px", fontSize: "13px" },
  resultKey: { color: "#64748b" },
  resultVal: { color: "#e2e8f0", fontWeight: 600 },
  downloadBtn: {
    width: "100%", padding: "12px", borderRadius: "10px",
    border: "1px solid #166534", background: "#0d1f0d",
    color: "#4ade80", fontSize: "13px", fontWeight: 600,
    cursor: "pointer", marginTop: "14px",
  },
  newBtn: {
    width: "100%", padding: "12px", borderRadius: "10px",
    border: "1px solid #2d2d4e", background: "#1a1a2e",
    color: "#94a3b8", fontSize: "13px", fontWeight: 600,
    cursor: "pointer", marginTop: "8px",
  },
};

export default function GrantAdmin() {
  const [secret, setSecret]           = useState("");
  const [grantType, setGrantType]     = useState("contributor");
  const [address, setAddress]         = useState("");
  const [egocAmount, setEgocAmount]   = useState("");
  const [vestMonths, setVestMonths]   = useState("0");
  const [cliffMonths, setCliffMonths] = useState("0");

  const chooseType = (type) => {
    setGrantType(type);
    if (type === "contributor") {
      setVestMonths("0");
      setCliffMonths("0");
    } else if (grantType === "contributor") {
      setVestMonths("24");
      setCliffMonths("6");
    }
  };
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState("");
  const [result, setResult]           = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!secret.trim())          { setError("Admin secret is required."); return; }
    if (!isMainnetAddress(address.trim())) {
      setError("Enter the recipient's mainnet address. It starts with ego1 and is 44 characters long; they can copy it in Ego Desktop under Wallet, Mainnet.");
      return;
    }
    if (!egocAmount || +egocAmount <= 0) { setError("Enter a valid EGOC amount."); return; }

    setError("");
    setLoading(true);

    try {
      const resp = await fetch(`${GRANT_API}/admin/create-grant`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-admin-secret": secret.trim(),
        },
        body: JSON.stringify({
          egoc_amount:  +egocAmount,
          grant_type:      grantType,
          mainnet_address: address.trim(),
          vest_months:  +vestMonths,
          cliff_months: +cliffMonths,
        }),
      });

      const data = await resp.json();
      if (!resp.ok || data.error) {
        setError(data.error || "Request failed.");
        setLoading(false);
        return;
      }
      setResult(data);
    } catch {
      setError("Network error — is the server running?");
    }

    setLoading(false);
  };

  const handleDownload = () => {
    if (!result) return;
    const blob = new Blob([JSON.stringify(result.voucher, null, 2)], { type: "application/json" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href     = url;
    a.download = result.filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const reset = () => {
    setResult(null);
    setAddress("");
    setEgocAmount("");
    setError("");
  };

  const capitalize = (s) => s.charAt(0).toUpperCase() + s.slice(1);

  return (
    <div style={s.page}>
      <div style={s.card}>
        <div style={s.header}>
          <div style={s.badge}>🔐 Admin — Grant Issuance</div>
          <h1 style={s.h1}>Issue a Grant</h1>
          <p style={s.subtitle}>Generate a signed grant voucher to send to a recipient. They upload it at <strong>/grant</strong> to activate their allocation.</p>
        </div>

        {!result ? (
          <form onSubmit={handleSubmit}>
            <div style={s.field}>
              <label style={s.label}>Admin Secret</label>
              <input
                type="password"
                style={s.input}
                placeholder="Your ADMIN_SECRET"
                value={secret}
                onChange={(e) => setSecret(e.target.value)}
                required
              />
              <div style={s.secretNote}>Never share this. Set via ADMIN_SECRET env var on the server.</div>
            </div>

            <hr style={s.divider} />

            <div style={s.field}>
              <label style={s.label}>Recipient Mainnet Address</label>
              <input
                type="text"
                style={s.input}
                placeholder="ego1..."
                value={address}
                onChange={(e) => setAddress(e.target.value.trim().toLowerCase())}
                spellCheck={false}
                autoComplete="off"
                required
              />
              <div style={s.secretNote}>The recipient copies this in Ego Desktop: Wallet, switch to Mainnet, Copy. The EGOC is credited to it in the genesis block.</div>
            </div>

            <div style={s.field}>
              <label style={s.label}>Grant Type</label>
              <select style={s.select} value={grantType} onChange={(e) => chooseType(e.target.value)}>
                {GRANT_TYPES.map((t) => (
                  <option key={t} value={t}>{capitalize(t)}</option>
                ))}
              </select>
            </div>

            <div style={s.field}>
              <label style={s.label}>EGOC Amount</label>
              <input
                type="number"
                style={s.input}
                placeholder="e.g. 50000"
                min="1"
                value={egocAmount}
                onChange={(e) => setEgocAmount(e.target.value)}
                required
              />
            </div>

            <div style={{ ...s.field, ...s.row }}>
              <div style={{ flex: 1 }}>
                <label style={s.label}>Vest Months</label>
                <input
                  type="number"
                  style={s.input}
                  min="0"
                  value={vestMonths}
                  onChange={(e) => setVestMonths(e.target.value)}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={s.label}>Cliff Months</label>
                <input
                  type="number"
                  style={s.input}
                  min="0"
                  value={cliffMonths}
                  onChange={(e) => setCliffMonths(e.target.value)}
                />
              </div>
            </div>

            <button type="submit" style={s.btn(loading)} disabled={loading}>
              {loading ? "Generating…" : "Generate Grant Voucher →"}
            </button>
            {error && <div style={s.errorBox}>{error}</div>}
          </form>
        ) : (
          <>
            <div style={s.result}>
              <div style={s.resultTitle}>✅ Grant Voucher Created</div>
              <div style={s.resultRow}>
                <span style={s.resultKey}>Recipient</span>
                <span style={{ ...s.resultVal, fontFamily: "monospace", fontSize: "12px", wordBreak: "break-all" }}>{result.voucher.mainnet_address || result.voucher.label || "—"}</span>
              </div>
              <div style={s.resultRow}>
                <span style={s.resultKey}>Type</span>
                <span style={s.resultVal}>{capitalize(result.voucher.grant_type)}</span>
              </div>
              <div style={s.resultRow}>
                <span style={s.resultKey}>Amount</span>
                <span style={s.resultVal}>{(result.voucher.allocation?.egoc_amount || 0).toLocaleString()} EGOC</span>
              </div>
              <div style={s.resultRow}>
                <span style={s.resultKey}>Vesting</span>
                <span style={s.resultVal}>{result.voucher.allocation?.vest_months}mo / {result.voucher.allocation?.cliff_months}mo cliff</span>
              </div>
              <div style={s.resultRow}>
                <span style={s.resultKey}>File</span>
                <span style={s.resultVal}>{result.filename}</span>
              </div>
            </div>

            <button style={s.downloadBtn} onClick={handleDownload}>
              ⬇ Download Voucher JSON
            </button>
            <button style={s.newBtn} onClick={reset}>
              + Issue Another Grant
            </button>
          </>
        )}
      </div>
    </div>
  );
}
